import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getCategoryColor } from '../utils/utils';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMaximize, FiNavigation, FiAlertCircle, FiCheckCircle, FiPlus } = FiIcons;

// Sub-component to handle map bounds and viewport events
const MapController = ({ filteredLocations, onViewportChange, onInteraction, lastSubmission }) => {
  const map = useMap();

  // Function to zoom to all markers
  const zoomToAll = useCallback(() => {
    if (filteredLocations.length === 0) return;
    const bounds = L.latLngBounds(filteredLocations.map(loc => [Number(loc.lat), Number(loc.lng)]));
    map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
  }, [filteredLocations, map]);

  // Handle zooming to a specific submission
  useEffect(() => {
    if (lastSubmission && lastSubmission.id) {
      const location = filteredLocations.find(l => l.id === lastSubmission.id || l.name === lastSubmission.name);
      if (location) {
        map.setView([Number(location.lat), Number(location.lng)], 16);
      }
    }
  }, [lastSubmission, filteredLocations, map]);

  // Monitor viewport changes and interactions
  useMapEvents({
    movestart: onInteraction,
    zoomstart: onInteraction,
    click: onInteraction,
    moveend: () => {
      const bounds = map.getBounds();
      const outside = filteredLocations.some(loc => !bounds.contains([Number(loc.lat), Number(loc.lng)]));
      onViewportChange(outside);
    }
  });

  return (
    <div className="absolute top-4 right-4 z-[1000] flex flex-col gap-2">
      <button
        onClick={zoomToAll}
        disabled={filteredLocations.length === 0}
        className={`flex items-center gap-2 px-4 py-2.5 rounded-lg shadow-xl font-bold transition-all duration-200 ${
          filteredLocations.length > 0 
            ? 'bg-white text-gray-800 hover:bg-gray-50 active:scale-95 border-2 border-gray-100' 
            : 'bg-gray-100 text-gray-400 cursor-not-allowed border-2 border-gray-200'
        }`}
      >
        <SafeIcon icon={FiMaximize} size={18} />
        Show all markers
      </button>
    </div>
  );
};

const createCustomIcon = (category) => {
  const color = getCategoryColor(category);
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${color}" width="36" height="36" stroke="white" stroke-width="1.5">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
      <circle cx="12" cy="9" r="2.5" fill="white"/>
    </svg>
  `;
  return L.divIcon({
    className: 'custom-leaflet-icon',
    html: svg,
    iconSize: [36, 36],
    iconAnchor: [18, 36],
    popupAnchor: [0, -36]
  });
};

const AtlasMap = () => {
  const [locations, setLocations] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedFocusArea, setSelectedFocusArea] = useState('All');
  const [loading, setLoading] = useState(true);
  const [outsideViewport, setOutsideViewport] = useState(false);
  const [isAlertDismissed, setIsAlertDismissed] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState(null);
  
  const centerPosition = [55.823957, 11.57];
  const API_URL = 'https://script.google.com/macros/s/AKfycbxwfV17nYG1oxE-DU9C-9h6rV1C3ITr0yBM2f6HGSiRt_mVnN4mHQ9aPKF8SjZqCRSm/exec';

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await fetch(API_URL);
        if (!response.ok) throw new Error('Failed to fetch data');
        const data = await response.json();
        const processedData = (Array.isArray(data) ? data : []).map(loc => ({
          ...loc,
          parsedFocusAreas: loc.focus_areas ? loc.focus_areas.split(',').map(item => item.trim()).filter(Boolean) : []
        }));
        setLocations(processedData);
      } catch (error) {
        console.error('Error fetching locations:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchLocations();
  }, []);

  // Reset alert dismissal state when filters change
  useEffect(() => {
    setIsAlertDismissed(false);
  }, [selectedCategory, selectedFocusArea]);

  const filteredLocations = useMemo(() => {
    return locations.filter(loc => {
      const categoryMatch = selectedCategory === 'All' || loc.category === selectedCategory;
      const focusMatch = selectedFocusArea === 'All' || loc.parsedFocusAreas.includes(selectedFocusArea);
      return categoryMatch && focusMatch;
    });
  }, [locations, selectedCategory, selectedFocusArea]);

  const dynamicCategories = useMemo(() => {
    const cats = new Set(locations.map(loc => loc.category).filter(Boolean));
    return ['All', ...Array.from(cats).sort()];
  }, [locations]);

  const dynamicFocusAreas = useMemo(() => {
    const areas = new Set();
    locations.forEach(loc => loc.parsedFocusAreas.forEach(area => areas.add(area)));
    return ['All', ...Array.from(areas).sort()];
  }, [locations]);

  const handleSimulateSubmission = (type) => {
    if (type === 'public') {
      setSubmissionStatus({ status: 'success', name: locations[0]?.name || 'Sample Site', id: locations[0]?.id });
    } else {
      setSubmissionStatus({ status: 'pending', name: 'New Community Hub' });
    }
  };

  return (
    <div className="flex flex-col h-full space-y-6 relative">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div className="space-y-4 flex-grow">
          <div>
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2 ml-1">Categories</h3>
            <div className="flex flex-wrap gap-2 pb-1 overflow-x-auto no-scrollbar">
              {dynamicCategories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 border whitespace-nowrap ${
                    selectedCategory === cat ? 'bg-gray-800 text-white border-gray-800 shadow-md' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {cat !== 'All' && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: getCategoryColor(cat) }}></span>}
                    {cat}
                  </div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2 ml-1">Focus Areas</h3>
            <div className="flex flex-wrap gap-2 pb-1 overflow-x-auto no-scrollbar">
              {dynamicFocusAreas.map((area) => (
                <button
                  key={area}
                  onClick={() => setSelectedFocusArea(area)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 border whitespace-nowrap ${
                    selectedFocusArea === area ? 'bg-blue-600 text-white border-blue-600 shadow-md' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-200'
                  }`}
                >
                  {area === 'All' ? 'All Focus Areas' : area}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        <div className="flex gap-2 shrink-0">
          <button 
            onClick={() => handleSimulateSubmission('public')}
            className="flex items-center gap-2 px-3 py-2 text-xs font-bold bg-green-50 text-green-700 rounded-lg border border-green-200 hover:bg-green-100 transition-colors"
          >
            <SafeIcon icon={FiPlus} size={14} /> Demo Success
          </button>
          <button 
            onClick={() => handleSimulateSubmission('pending')}
            className="flex items-center gap-2 px-3 py-2 text-xs font-bold bg-amber-50 text-amber-700 rounded-lg border border-amber-200 hover:bg-amber-100 transition-colors"
          >
            <SafeIcon icon={FiPlus} size={14} /> Demo Pending
          </button>
        </div>
      </div>

      <div className="flex-grow w-full rounded-xl overflow-hidden shadow-lg border border-gray-200 relative min-h-[450px]">
        {loading && (
          <div className="absolute inset-0 z-[1000] bg-white/80 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800"></div>
          </div>
        )}

        {submissionStatus && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[2000] w-[90%] max-w-md animate-in fade-in slide-in-from-top-4 duration-300">
            <div className={`p-4 rounded-xl shadow-2xl border-2 flex items-center justify-between gap-4 ${
              submissionStatus.status === 'success' ? 'bg-white border-green-500' : 'bg-white border-amber-400'
            }`}>
              <div className="flex items-center gap-3">
                <SafeIcon 
                  icon={submissionStatus.status === 'success' ? FiCheckCircle : FiAlertCircle} 
                  className={submissionStatus.status === 'success' ? 'text-green-500' : 'text-amber-500'} 
                  size={24} 
                />
                <div>
                  <p className="font-bold text-gray-900 text-sm">
                    {submissionStatus.status === 'success' ? 'Listing Published!' : 'Submitted for Approval'}
                  </p>
                  <p className="text-xs text-gray-500">{submissionStatus.name}</p>
                </div>
              </div>
              <div className="flex gap-2">
                {submissionStatus.status === 'success' && (
                  <button 
                    onClick={() => {
                      const trigger = submissionStatus;
                      setSubmissionStatus(null);
                      setTimeout(() => setSubmissionStatus(trigger), 10);
                    }}
                    className="px-3 py-1.5 bg-gray-900 text-white rounded-lg text-xs font-bold hover:bg-black transition-colors"
                  >
                    Go to my submission
                  </button>
                )}
                <button 
                  onClick={() => setSubmissionStatus(null)}
                  className="p-1.5 text-gray-400 hover:text-gray-600"
                >
                  <FiIcons.FiX size={18} />
                </button>
              </div>
            </div>
            {submissionStatus.status === 'pending' && (
              <div className="mt-2 text-center">
                <p className="text-[11px] bg-amber-50 text-amber-700 px-3 py-1 rounded-full inline-block font-medium border border-amber-100 shadow-sm">
                  Note: Your listing will appear on the map after moderator review.
                </p>
              </div>
            )}
          </div>
        )}

        {outsideViewport && !isAlertDismissed && !loading && filteredLocations.length > 0 && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000] animate-in fade-in slide-in-from-top-2 duration-300">
            <div className="bg-gray-900/90 backdrop-blur-md text-white px-4 py-2 rounded-full flex items-center gap-3 shadow-2xl border border-white/20 pointer-events-none">
              <SafeIcon icon={FiNavigation} size={14} className="text-blue-400 rotate-45" />
              <span className="text-[11px] font-bold tracking-wide uppercase">
                Some listings are outside the current map view. Zoom out or tap ‘Show all markers’.
              </span>
            </div>
          </div>
        )}
        
        <MapContainer center={centerPosition} zoom={13} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
          <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          
          <MapController 
            filteredLocations={filteredLocations} 
            onViewportChange={setOutsideViewport}
            onInteraction={() => setIsAlertDismissed(true)}
            lastSubmission={submissionStatus?.status === 'success' ? submissionStatus : null}
          />

          {filteredLocations.map((loc) => (
            <Marker key={loc.name} position={[Number(loc.lat), Number(loc.lng)]} icon={createCustomIcon(loc.category)}>
              <Popup className="custom-popup">
                <div className="p-1 min-w-[200px]">
                  <div className="flex flex-wrap gap-1 mb-2">
                    <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold text-white uppercase tracking-wide" style={{ backgroundColor: getCategoryColor(loc.category) }}>
                      {loc.category}
                    </span>
                  </div>
                  <h3 className="font-bold text-base text-gray-900 m-0 leading-tight">{loc.name}</h3>
                  <p className="text-gray-600 text-xs mt-2 mb-3 leading-relaxed">{loc.description}</p>
                  {(loc.address || loc.contact_email) && (
                    <div className="mb-3 flex flex-col gap-2 py-2 border-y border-gray-50">
                      {loc.address && (
                        <div className="flex items-start gap-2 text-gray-500 text-[10px]">
                          <SafeIcon name="MapPin" className="mt-0.5 shrink-0 text-gray-400" size={12} />
                          <span className="leading-tight">{loc.address}</span>
                        </div>
                      )}
                      {loc.contact_email && (
                        <div className="flex items-start gap-2 text-blue-600 text-[10px]">
                          <SafeIcon name="Mail" className="mt-0.5 shrink-0" size={12} />
                          <a href={`mailto:${loc.contact_email}`} className="hover:underline truncate font-medium">{loc.contact_email}</a>
                        </div>
                      )}
                    </div>
                  )}
                  {loc.parsedFocusAreas.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {loc.parsedFocusAreas.map(area => (
                        <span key={area} className="text-[9px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-sm font-medium">#{area}</span>
                      ))}
                    </div>
                  )}
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </div>
  );
};

export default AtlasMap;